// Sample hero / job definitions and a battle-ready Entity factory. makeHero
// composes a CharacterSetup, resolves stats, and attaches the chosen item-skill
// IDs to the entity.
//
// ADR 0025: WARRIOR / MAGE / CLERIC now alias the canonical catalogue
// (`@renavora/content` `JOBS_BY_ID`). The local stub copies that used to live
// here have been removed (ADR 0006 §5.3 follow-up). Consumers (cli, golden
// tests, smoke bench) get real growth bands, real `ult.<job>.<name>` ultimates
// and real Type tags. Note: content Tier 1 jobs intentionally hold no Type
// tags (Type is established at Tier 2 per ADR 0005 §2.2), so the catalogue
// `warrior` has `types: []`.
import { JOBS_BY_ID } from '@renavora/content';
import { aggregateDungeonBoons, } from '@renavora/shared';
import { deriveWeaponElement } from '../equipment-affixes.js';
import { resolveStats } from '../stats-resolver.js';
function requireJob(id) {
    const job = JOBS_BY_ID.get(id);
    if (job === undefined)
        throw new Error(`heroes.ts: unknown job id "${id}"`);
    return job;
}
export const WARRIOR = requireJob('warrior');
export const MAGE = requireJob('mage');
export const CLERIC = requireJob('cleric');
export const SAMPLE_JOBS = [WARRIOR, MAGE, CLERIC];
/** Build a battle-ready Entity from a hero / job setup. */
export function makeHero(opts) {
    const stats = resolveStats(opts.setup);
    const boons = aggregateDungeonBoons(opts.setup.boons);
    const skillIds = opts.itemSkillIds ?? [];
    const itemSkills = opts.itemSkills?.map((skill) => ({
        ...skill,
        activation: structuredClone(skill.activation),
        values: [...skill.values],
    })) ??
        skillIds.map((id) => ({
            templateId: id,
            kind: 'active',
            activation: {},
            values: [],
        }));
    return {
        id: opts.id,
        side: opts.side ?? 'ally',
        level: opts.setup.jobLevel,
        element: opts.element ?? 'physical',
        weaponElement: opts.weaponElement ?? deriveWeaponElement(opts.setup.equipment),
        pos: opts.pos ?? { x: 0, y: 0 },
        radius: 0.5,
        velocity: { x: 0, y: 0 },
        hp: stats.hpMax,
        hpMax: stats.hpMax,
        shields: [],
        stats,
        jobTypes: new Set(opts.setup.job.types),
        cooldowns: new Map(),
        buffs: [],
        debuffs: [],
        ultimate: opts.setup.job.ultimateSkillId,
        itemSkills,
        behaviorRules: opts.behaviorRules ? [...opts.behaviorRules] : defaultRules(opts.setup.job),
        speedMods: {
            actionSpeed: 0,
            attackSpeed: 0,
            cooldownRate: boons.cooldownReduction,
            moveSpeed: 0,
        },
        damageReduction: boons.damageReduction,
        summonPowerBonus: boons.summonPowerBonus,
        placeablePowerBonus: boons.placeablePowerBonus,
        threatMod: 1,
    };
}
/** Job-typical default rules. Healers anchor and watch ally HP, melee sticks
 *  to the target, casters keep distance. */
function defaultRules(job) {
    if (job.types.includes('Healer')) {
        return [
            { category: 'targeting', ruleId: 'target_nearest' },
            { category: 'movement', ruleId: 'move_anchor_lock' },
            { category: 'tempo', ruleId: 'tempo_burst' },
            { category: 'threatPolicy', ruleId: 'threat_low' },
            { category: 'positionPolicy', ruleId: 'pos_safe_line' },
        ];
    }
    if (job.types.includes('MagicDPS') || job.types.includes('RangedDPS')) {
        return [
            { category: 'targeting', ruleId: 'target_nearest' },
            { category: 'movement', ruleId: 'move_distance_mid' },
            { category: 'tempo', ruleId: 'tempo_burst' },
            { category: 'threatPolicy', ruleId: 'threat_neutral' },
            { category: 'positionPolicy', ruleId: 'pos_behind_tank_anchor' },
        ];
    }
    // Tank / MeleeDPS default.
    return [
        { category: 'targeting', ruleId: 'target_nearest' },
        { category: 'movement', ruleId: 'move_melee_stick' },
        { category: 'tempo', ruleId: 'tempo_burst' },
        {
            category: 'threatPolicy',
            ruleId: job.types.includes('Tank') ? 'threat_high' : 'threat_neutral',
        },
        { category: 'positionPolicy', ruleId: 'pos_behind_target' },
    ];
}
//# sourceMappingURL=heroes.js.map